import { useEffect, useState } from "react"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-pencil.svg"
import { FeedbackQuestionType } from "../common/constants"
import { Each } from '../common/Each'
import useContextMenu from "../common/hooks/useContextMenu"
import typo from "../typography.module.css"
import { formatDateV2, formatTimeV2 } from "../utils"
import Card from "./cards/Card"
import Dialog from "./dialogs/Dialog"
import FeedbackViewer from "./dialogs/FeedbackViewer"
import styles from "./FeedbacksLessons.module.css"
import RoundBadge from "./RoundBadge"
import TextInput from "./TextInput"
import api from './../api';
import Select from "./Select"
import Table from "./Table"
import StatusBadge from "./StatusBadge"
import MeatBallsMenu from "./MeatBallsMenu"


const FeedbacksLessons = ({ feedbacks = [], feedbacksLoading = true, onEdit = () => { }, onDelete = () => { } }) => {

	const { showMenu } = useContextMenu()

	const [filteredFeedbacks, setFilteredFeedbacks] = useState([])
	const [students, setStudents] = useState([])
	const [filteredStudents, setFilteredStudents] = useState([])
	const [, setLoading] = useState(true)
	const [openViewerDialog, setOpenViewerDialog] = useState(false)

	const [selectedFeedback, setSelectedFeedback] = useState(null)
	const [content, setContent] = useState(null)
	const [selectedUserFeedback, setSelectedUserFeedback] = useState(null)

	const [courses, setCourses] = useState([])
    const [courseSearch, setCourseSearch] = useState("")
	const [loadingFilters, setLoadingFilters] = useState({ courses: false, editions: false, modules: false })

	const [selectedCourse, setSelectedCourse] = useState(null)
	const [selectedEdition, setSelectedEdition] = useState(null)
	const [selectedModule, setSelectedModule] = useState(null)
	const [selectedLesson, setSelectedLesson] = useState(null)

	useEffect(() => {
		const getCourses = async () => {
			setLoadingFilters(l => ({ ...l, courses: true }))
			try {
				const courses = await api.get("/admin/courses")
				const c = [...courses].sort((a, b) => (a.name || "").localeCompare(b.name || "", "it", { sensitivity: "base", numeric: true })).map(c => ({
					id: c.id,
					name: c.name,
					value: c.id,
				}))
				c.unshift({ id: -1, name: "Tutti i corsi", value: null })
				setCourses(c)
				setSelectedCourse(c[0])
			} catch (e) {
				console.error(e)
			}
			setLoadingFilters(l => ({ ...l, courses: false }))
		}
		getCourses()

	}, [])

	useEffect(() => {
		setSelectedEdition(null)
		setSelectedModule(null)
		setSelectedLesson(null)
		if (!selectedCourse || !selectedCourse.value) {
			return
		}

		let course = courses.find(c => c.id === selectedCourse.value)
		const getEditions = async () => {
			setLoadingFilters(l => ({ ...l, editions: true }))
			try {
				const editions = await api.get(`/admin/courses/${selectedCourse.id}/editions`)
				const e = editions.map(e => ({
					id: e.id,
					name: e.name,
					value: e.id,
				}))
				e.unshift({ id: -1, name: "Tutte le edizioni", value: null })
				course.editions = e
				setCourses([...courses])
			} catch (e) {
				console.error(e)
			}
			setLoadingFilters(l => ({ ...l, editions: false }))
		}

		getEditions()
	}, [selectedCourse])

	useEffect(() => {
		setSelectedModule(null)
		setSelectedLesson(null)
		if (!selectedEdition || !selectedEdition.value) {
			return
		}
		const course = courses.find(c => c.id === selectedCourse.value)
		const edition = course.editions.find(e => e.id === selectedEdition.value)
		const getModules = async () => {
			setLoadingFilters(l => ({ ...l, modules: true }))
			try {
				const response = await api.get(`/admin/editions/${selectedEdition.id}`)
				const m = response.modules.map(m => {
					const lessons = m.lessons
					lessons.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
					lessons.unshift({ id: -1, name: "Tutti le lezioni", value: null })
					return {
						id: m.id,
						name: m.name,
						value: m.id,
						lessons
					}
				})
				m.unshift({ id: -1, name: "Tutti i moduli", value: null })
				edition.modules = m
				setCourses([...courses])
			} catch (e) {
				console.error(e)
			}
			setLoadingFilters(l => ({ ...l, modules: false }))
		}

		getModules()

	}, [selectedEdition])

	useEffect(() => {
		setSelectedLesson(null)
		if (!selectedModule || !selectedModule.value) {
			return
		}
	}, [selectedModule])

	useEffect(() => {
		setLoading(true)
		const newFeedbacks = []
		for (const feedback of feedbacks) {
			let sum = 0
			let count = 0
			for (const user_feedback of feedback.users_feedback) { // somma tutte le risposte numeriche di tutti gli studenti
				const answers = user_feedback.content.filter(c => [FeedbackQuestionType.Qualitative, FeedbackQuestionType.Quantitative].includes(c.type)).map(q => q.answer)
				sum += answers.reduce((a, c) => a += c, 0)
				count += answers.length
			}
			feedback.average = count > 0 ? Math.round(sum / count * 10) / 10 : 0
			feedback.numberOfFeedbacks = feedback.users_feedback.length ?? 0
			newFeedbacks.push(feedback)
		}
		setFilteredFeedbacks(newFeedbacks)
		setLoading(false)
	}, [feedbacks])

	useEffect(() => {

		if (!selectedFeedback) {
			setContent(null)
			return
		}
		let studentsFeedback = selectedFeedback.users_feedback
		if (selectedCourse?.value) {
			studentsFeedback = studentsFeedback.filter(uf => uf.course.id === selectedCourse.value)
		}

		if (selectedEdition?.value) {
			studentsFeedback = studentsFeedback.filter(uf => uf.edition.id === selectedEdition.value)
		}

		if (selectedModule?.value) {
			studentsFeedback = studentsFeedback.filter(uf => uf.module.id === selectedModule.value)
		}

		if (selectedLesson?.id && selectedLesson.id !== -1) {
			studentsFeedback = studentsFeedback.filter(uf => uf.resource_id === selectedLesson.id)
		}

		let students = {}
		const feedbacksAverage = (feedbacks) => {
			const answers = feedbacks.map(f => f.content.filter(c => [FeedbackQuestionType.Qualitative, FeedbackQuestionType.Quantitative].includes(c.type)).map(q => q.answer)).flat()
			return average(answers)
		}

		for (const studentFeedback of studentsFeedback) {
			const { user, ...feedback } = studentFeedback
			if (!students[user.id]) {
				students[user.id] = { ...user, average: feedbacksAverage([feedback]), feedbacks: [feedback] };
			} else {
				students[user.id].feedbacks.push(feedback)
				students[user.id].average = feedbacksAverage(students[user.id].feedbacks)
			}
		}
		students = Object.values(students)
		// Helper: most recent feedback timestamp for a student
		const getLastFeedbackTs = (s) => {
			if (!s?.feedbacks?.length) return 0
			return Math.max(
				...s.feedbacks.map(f => {
					const d = f?.created_at || f?.updated_at || f?.date || 0
					const ts = new Date(d).getTime()
					return Number.isFinite(ts) ? ts : 0
				})
			)
		}
		// Attach last_feedback_at (ISO string) to each student
		students = students.map(s => {
			const ts = getLastFeedbackTs(s)
			return { ...s, last_feedback_at: ts ? new Date(ts).toISOString() : null }
		})
		// Sort students by the date of their most recent feedback (descending)
		students = students.sort((a, b) => getLastFeedbackTs(b) - getLastFeedbackTs(a))
		const content = []
		for (const item of selectedFeedback.content) {
			const answers = students // per ogni studente
				.filter(s => s.feedbacks) // prendo quelli che hanno dato un feedback
				.map(s => s.feedbacks // per ogni feedback dello studente
					.map(f => f.content.filter(c => c.id === item.id)) // dal loro feedback prendo la domanda uguale ad item
					.filter(c => c.length > 0) // se esiste
					.map(c => c[0].answer) // prendo la risposta
				).flat()

			item.average = average(answers)
			item.answers = answers
			content.push(item)
		}

		for (const question of content) { // calcola percentuale per ogni risposta di ogni domanda
			const occurences = new Array(5).fill(0)
			let sum = 0
			for (const a of [1, 2, 3, 4, 5]) {
				occurences[a - 1] = question.answers.reduce((acc, c) => c === a ? acc + 1 : acc, 0)
				sum += occurences[a - 1]
			}
			question.percentages = occurences.map(o => sum ? Math.round((o / sum) * 100) : 0)
		}


		if (selectedCourse?.value) {
			const allAnswers = content.map(c => c.answers).flat()
			const globalAverage = average(allAnswers)
			const numberOfFeedbacks = students.filter(s => s.feedbacks).length
			// Update corresponding feedback entry without depending on filteredFeedbacks in closure
			setFilteredFeedbacks(prev => prev.map(ff => ff.id === selectedFeedback.id
				? { ...ff, average: globalAverage, numberOfFeedbacks }
				: ff
			))
		}

		if (!selectedCourse?.value) {
			const newFeedbacks = []
			for (const feedback of feedbacks) {
				let sum = 0
				let count = 0
				for (const user_feedback of feedback.users_feedback) { // somma tutte le risposte numeriche di tutti gli studenti
					const answers = user_feedback.content.filter(c => [FeedbackQuestionType.Qualitative, FeedbackQuestionType.Quantitative].includes(c.type)).map(q => q.answer)
					sum += answers.reduce((a, c) => a += c, 0)
					count += answers.length
				}
				feedback.average = count > 0 ? Math.round(sum / count * 10) / 10 : 0
				feedback.numberOfFeedbacks = feedback.users_feedback.length ?? 0
				newFeedbacks.push(feedback)
			}
			setFilteredFeedbacks(newFeedbacks)
		}

		setStudents(students)
		setFilteredStudents(students)
		setContent(content)
	}, [selectedFeedback, selectedCourse, selectedEdition, selectedModule, selectedLesson, feedbacks])

	const onContextMenu = (e, feedback) => {
		let actions = [
			{ name: "Modifica", icon: <EditIcon className={styles.icon} style={{ color: "var(--sf-light-green)" }} />, onClick: () => onEdit(feedback) },
			{ name: "Elimina", icon: <DeleteIcon className={styles.icon} style={{ color: "var(--sf-red)" }} />, onClick: () => onDelete(feedback) },
		]

		if (feedback.status === 'public') {
			actions = actions.filter(a => a.name !== "Elimina")
		}
		showMenu(e, actions)
	}

	const average = (arr) => {
		arr = arr.filter(a => a > 0)
		return arr.length > 0 ? Math.round(arr.reduce((a, c) => a += c, 0) / arr.length * 10) / 10 : 0
	}

	const getBadgeColor = (s) => {
		switch (s) {
			case "hidden": return "var(--sf-red)";
			case "draft": return "var(--sf-purple)";
			case "public": return "var(--sf-light-green)";
			default: return "black";
		}
	}

	const getAverageColor = (avg) => {
		if (!avg)
			return "var(--sf-gray)"

		if (avg < 2.5) {
			return "var(--sf-red)"
		}
		if (avg < 4) {
			return "var(--sf-yellow)"
		}
		if (avg >= 4)
			return "var(--sf-green)"
	}

	const handleClick = (feedback) => {
		setSelectedEdition(null)
		setSelectedModule(null)
		setSelectedLesson(null)
		setSelectedFeedback(feedback)
	}

	const onSearch = (text) => {
        setFilteredStudents(students.filter(student => `${student.name} ${student.surname}`.toLowerCase().includes(text.trim().toLowerCase())))
    }

	const getHeight = (question, answer) => {
		return `${(question.percentages[answer - 1] || 0) * 40 / 100}px`
	}

	return (
		<>
			<div className={styles.list}><Card appearance="modern" integratedTable title="Feedback lezioni">
				<Table appearance="minimal"
					onRowClick={handleClick}
					onContextMenu={onContextMenu}
					loading={feedbacksLoading}
					loadingN={3}
					style={{ borderRadius: 0, border: 0, marginTop: 0 }}
					columns={
						[
							{ key: 'name', label: 'NOME', type: "string" },
							{ key: 'created_at', label: "DATA CREAZIONE", type: "datetime", options: { day: 'numeric', month: 'short', year: 'numeric' } },
							{ key: 'numberOfFeedbacks', label: 'NUMERO FEEDBACK', type: "number" },
							{ key: '_average', label: 'MEDIA VOTI', type: "custom" },
							{ key: '_status', label: 'STATO', type: 'custom' },
							{ key: 'edit', label: 'AZIONI', type: 'custom', handleClick: true }
						]
					}
					elements={filteredFeedbacks.map((f) => ({
						...f,
						_status: { value: f.status, type: 'string', component: <StatusBadge appearance="soft" color={getBadgeColor(f.status)} label={{ public: "Pubblico", draft: "Bozza", hidden: "Nascosto" }[f.status] || f.status} /> },
						_average: { value: f.average, type: 'number', component: <span style={{ color: getAverageColor(f.average), fontWeight: 600 }}>{f.average}</span> },
						edit: <MeatBallsMenu actions={[{ label: 'Modifica', icon: EditIcon, onClick: () => onEdit(f) }, ...(f.status !== 'public' ? [{ label: 'Elimina', icon: DeleteIcon, onClick: () => onDelete(f), style: { color: 'var(--sf-red)' } }] : [])]} />
					}))}
				/>
			</Card></div>

			{selectedFeedback &&
				<Dialog appearance="modern"
				open={!!selectedFeedback} 
				title={selectedFeedback.name} 
				onClose={() => setSelectedFeedback(null)} 
				style={{ width: "min(1180px, 94vw)" }}>
					<div className={styles.collapsable}>
						<div className={styles.studentsContainer}><h3 className={styles.panelTitle}>Partecipanti</h3>
							<div style={{ padding: ".5rem", paddingTop: ".5rem", paddingBottom: 0 }}>
								<TextInput appearance="modern"
									type="search"
									onKeyUp={onSearch}
									placeholder="Cerca partecipante…"
									disabled={students.length === 0}
								/>
							</div>
							<div className={styles.students}>
								<Table appearance="minimal"
									onRowClick={(element) => {
										setSelectedUserFeedback(element)
										setOpenViewerDialog(true)
									}}
									style={{ borderRadius: 0, marginTop: 0 }}
									columns={[
										{ key: 'student', label: 'STUDENTE', type: "custom", wrap: true },
										{ key: 'number', label: 'NUMERO FEEDBACK', type: "number", style: { justifyContent: 'center' } },
										{ key: '_average', label: 'MEDIA VOTI', type: "custom", style: { justifyContent: 'center' } },
									]}
									elements={filteredStudents.map((f) => ({
										...f,
										student: {
											value: `${f.name} ${f.surname}`, type: "string", component:
												<div style={{ display: 'flex', flexDirection: 'column' }} onClick={() => {
													setSelectedUserFeedback(f)
													setOpenViewerDialog(true)

												}}>
													<div>{`${f.name} ${f.surname}`}</div>
													<div className={typo.caption}>{formatDateV2(f.last_feedback_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} - {formatTimeV2(f.last_feedback_at)}</div>
												</div>
										},
										number: f.feedbacks?.length ?? 0,
										_average: { value: f.average, type: 'number', component: <span style={{ color: getAverageColor(f.average), fontWeight: 600 }}>{f.average ?? "-"}</span> },
									}))}
								/>
							</div>
						</div>
						<div className={styles.questionsContainer}>
							<div className={styles.filters}>
								<div className={styles.filter}>
									<Select appearance="modern" portal
										options={courses.filter(course => course.value === null || course.name.toLocaleLowerCase('it').includes(courseSearch.trim().toLocaleLowerCase('it')))}
                                        onSearch={setCourseSearch}
                                        searchPlaceholder="Cerca corso…"
										selected={selectedCourse}
										onSelect={v => setSelectedCourse(v)}
										loading={loadingFilters.courses}
									/>
								</div>
								<div className={styles.filter}>
									<Select appearance="modern" portal
										disabled={!selectedCourse?.value || loadingFilters.editions}
										options={selectedCourse?.editions ?? []}
										selected={selectedEdition}
										onSelect={v => setSelectedEdition(v)}
										placeholder={"Nessun corso selezionato"}
										loading={loadingFilters.editions}
									/>
								</div>
								<div className={styles.filter}>
									<Select appearance="modern" portal
										disabled={!selectedEdition?.value || loadingFilters.modules}
										options={selectedEdition?.modules ?? []}
										selected={selectedModule}
										onSelect={v => setSelectedModule(v)}
										placeholder={"Nessuna edizione selezionata"}
										loading={loadingFilters.modules}
									/>
								</div>
								<div className={styles.filter}>
									<Select appearance="modern" portal
										disabled={!selectedModule?.value}
										options={selectedModule?.lessons ?? []}
										selected={selectedLesson}
										onSelect={v => setSelectedLesson(v)}
										placeholder={"Nessun modulo selezionato"}
									/>
								</div>
							</div>

							<div className={styles.questions}>
								<div className={styles.description}>{selectedFeedback?.description}</div>
								<Each
									of={content ?? []}
									render={(item, index) => (
										<div className={styles.questionContainer}>
											<div className={styles.question}>{index + 1}. {item.question}</div>
											<div className={styles.answers}>
												<Each
													of={[1, 2, 3, 4, 5]}
													render={(answer) => (
														<div className={styles.answer} >
															<div className={styles.answerBackground} style={{ height: getHeight(item, answer) }} />
															<div className={styles.answerValue}>{answer}</div>
														</div>
													)}
												/>
											</div>
											<RoundBadge color={getAverageColor(item.average)}>{item.average}</RoundBadge>
										</div>
									)}
								/>
							</div>
						</div>
					</div>
				</Dialog>
			}
			<Dialog appearance="modern" style={{ width: "min(760px, 94vw)" }}
				open={openViewerDialog}
				onClose={() => {
					setOpenViewerDialog(false)
					setSelectedUserFeedback(null)
				}}
				title={selectedUserFeedback ? `${selectedUserFeedback.name} ${selectedUserFeedback.surname}` : ""}
				contentMaxHeight="80dvh"
				action={
					<div className={styles.dialogAction}>
					</div>
				}
			>
				<FeedbackViewer userFeedback={selectedUserFeedback} feedback={selectedFeedback} />
			</Dialog>
		</>
	)
}

export default FeedbacksLessons
